/**
 * URL Session Store
 *
 * This is a small library that uses sessionStorageEx to store temporary state data.
 * You can save or retrieve data chunks from the store provided by this library.
 * Store created by this library has a unique session id that will be passesd through the URL querystring.
 *
 * ## Use case
 *
 * When you have an FE-only workflow, during which many data need to be stored during the workflow.
 * The common solution is to pass the data in the querystring.
 * However this may cause issues when there are too many data, since URL has a length limit.
 * In this case, this library provides a storage that can used to store arbitrary amount of data,
 * while keeps the URL querystring short (only a session id).
 *
 * ## Why need a session id? Can we simply store data in sessionStorage?
 *
 * The reason for using session id is to support multi-workflow from multiple windows.
 * Considier that the same user opens the same workflow in two different windows.
 * We want the two windows to have different workflows, instead of interfering with each other.
 *
 * ## Usage
 *
 * 1. Wrap your component with `withUrlSessionStore`.
 *    type UrlSessionState = {
 *      stateNeedsToBePersisted?: string
 *    };
 *
 *    return compose(
 *      withUrlSessionStore<UrlSessionState>(),
 *    )(MyComponent)
 *
 * 2. The UrlSessionStore is placed in context and can be accessed with `connectToUrlSessionStore` HOC:
 *
 *    type Props = {
 *      ...your props
 *    } & PropsFromSessionStore<UrlSessionState>;
 *
 *    type State = {
 *       stateNeedsToBePersisted: string;
 *    }
 *
 *    componentDidMount() {
 *      const { sessionStore } = this.props;
 *      const { stateNeedsToBePersisted } = sessionStore;
 *
 *      // copy state from session to local state
 *      if (stateNeedsToBePersisted) {
 *        this.setState({ stateNeedsToBePersisted });
 *      }
 *    }
 *
 *    onClick = () => {
 *      const { stateNeedsToBePersisted } = this.state;
 *      const { sessionStore, pushWithSession, router } = this.props;
 *      sessionStore.setState({ stateNeedsToBePersisted });
 *      pushWithSession({ name: 'another-url', params: router.params });
 *    }
 *
 *    ...
 *    export default compose<Props, {}>(
 *      ...
 *      connectToUrlSessionStore(UrlSessionState, Props)(),
 *    )(MyComponent);
 *
 *  **NOTE**: By default, `connectToUrlSessionStore` guarantee that the store is ready to use when the wrapped
 *            component is rendering. So it is safe to call `sessionStore.getState()` whereever you like.
 *            However, if you need to render `MyComponent` suring the wrapper mounting period (though very short period),
 *            you can use `connectTourlSessionStore(true)` instead.
 *            This will pass a `mounting` prop to the wrapped component, which indicates whether the store is ready to use.
 *            DO NOT call `sessionStore.getState()` or `sessionStore.setState()` if `mounting === true`.
 *            In this case, usually it is safe to call `sessionStore.getState()` in `MyComponent.componentDidMount()`.
 *
 */
import * as React from 'react';

import PropTypes from 'prop-types';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';

import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';

const SESSION_STORAGE_KEY = '_urlSessionStorage:';
export const URL_SESSION_KEY = '_usid';

const getKey = (sessionId: string) => `${SESSION_STORAGE_KEY}${sessionId}`;

const randomString = () => Math.random().toString(36).split('.')[1];

class UrlSessionStore<T> {
  declare sessionId: string;

  constructor(sessionId?: string) {
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    this.sessionId = sessionId ?? randomString();
  }

  checkSSR() {
    if (typeof window === 'undefined') {
      throw new Error(
        'UrlSessionStore can only be accessed in `componentDidMount`. Please copy the state to local state in `componentDidMount`.'
      );
    }
  }

  getState(): T {
    this.checkSSR();

    const serializedState = sessionStorageEx.getItem(getKey(this.sessionId), JSON.parse, {});
    return serializedState as T;
  }

  setState(newState: Partial<T>) {
    this.checkSSR();

    const state = this.getState();
    sessionStorageEx.setItem(getKey(this.sessionId), { ...state, ...newState }, JSON.stringify);
  }

  clearState() {
    this.checkSSR();
    sessionStorageEx.removeItem(getKey(this.sessionId));
  }

  getUrlParams() {
    return { [URL_SESSION_KEY]: this.sessionId };
  }
}

type PushFunc = InjectedRouter['push'];

export type UrlSessionStoreContext<T> = {
  sessionStore: UrlSessionStore<T>;
  pushWithSession: PushFunc;
};

export const getUrlSessionStore = <T>(sessionId?: string) => new UrlSessionStore<T>(sessionId);

export const getUrlSessionStoreFromUrlParams = <T>(query: object): UrlSessionStore<T> => {
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const sessionId = query[URL_SESSION_KEY];
  return getUrlSessionStore<T>(sessionId);
};

type Context = {
  router: InjectedRouter;
};

export const UrlSessionStoreInternalContext = React.createContext<UrlSessionStoreContext<unknown>>(
  {} as UrlSessionStoreContext<unknown>
);

const withUrlSessionStore = <T, Props>() => {
  return (Component: React.ComponentClass<Props> | React.FC<Props>) => {
    const componentName = Component.displayName || Component.name;

    const UrlSessionStoreConnector = class extends React.Component<Props> {
      store: UrlSessionStore<T>;

      static displayName = componentName + 'UrlSessionStoreConnector';

      static contextTypes = {
        router: PropTypes.object,
      };

      declare context: Context;

      static childContextTypes = {
        sessionStore: PropTypes.object,
        pushWithSession: PropTypes.func,
      };

      getChildContext() {
        const { router } = this.context;
        const push: PushFunc = (pathOrLoc: $TSFixMe) => {
          let newPathOrLoc: typeof pathOrLoc;

          if (typeof pathOrLoc === 'string') {
            newPathOrLoc = pathOrLoc;
          } else {
            // newPathOrLoc uses default values from:
            // (1) router  (2) pathOrLoc
            // then add the getUrlParams() as query
            newPathOrLoc = {
              params: router?.params,
              ...pathOrLoc,

              query: {
                ...router?.location.query,
                ...pathOrLoc.query,
                ...this.store.getUrlParams(),
              },
            };
          }

          router?.push(newPathOrLoc);
        };

        return {
          sessionStore: this.store,
          pushWithSession: push,
        };
      }

      constructor(props: Props, context: Context) {
        super(props);

        const { router } = context;
        const store = getUrlSessionStoreFromUrlParams<T>(router.location.query);

        this.store = store;
      }

      render() {
        // Copy the legacy context to the new context API so that the context can be
        // retrieved by the `useUrlSessionStore`.
        return React.createElement(
          UrlSessionStoreInternalContext.Provider,
          {
            value: this.getChildContext(),
          },
          // @ts-expect-error TSMIGRATION-4.8 TS2769
          React.createElement(Component, { ...this.props })
        );
      }
    };

    hoistNonReactStatics(UrlSessionStoreConnector, Component);

    return UrlSessionStoreConnector;
  };
};

export type PropsFromSessionStore<USS> = {
  pushWithSession: PushFunc;
  sessionStore: UrlSessionStore<USS>;
};

/**
 * Props: the input props type of the HOC
 *
 * output props = Props & PropsFromSessionSTore<UrlSessionStore>
 */

export const connectToUrlSessionStore = <USS, Props extends {}>(useSSR = false) => {
  return (Component: React.ComponentClass<Props> | React.FC<Props>) => {
    const componentName = Component.displayName || Component.name;

    const UrlSessionStateConnector = class extends React.Component<Props> {
      static displayName = componentName + 'UrlSessionStateConnector';

      static contextTypes = {
        sessionStore: PropTypes.object,
        pushWithSession: PropTypes.func,
      };

      declare context: PropsFromSessionStore<USS>;

      render() {
        const { pushWithSession, sessionStore } = this.context;

        const outProps: PropsFromSessionStore<USS> = {
          pushWithSession,
          sessionStore,
        };

        // If the page does not need to use SSR, and the code is rendered on server,
        // then we skip rendering the children
        if (!useSSR && typeof window === 'undefined') {
          return null;
        }

        return React.createElement(Component, {
          ...this.props,
          ...outProps,
        });
      }
    };

    hoistNonReactStatics(UrlSessionStateConnector, Component);

    return UrlSessionStateConnector;
  };
};

export default withUrlSessionStore;
